import { cloneDeep } from 'es-toolkit/compat';
import { FC, Reducer, useReducer } from 'react';

import { PropsWithOnlyChildren } from 'app/types';

import {
  NavigationContextAction,
  NavigationDispatchContext,
  NavigationState,
  NavigationStateContext,
} from './contexts';
import { initialQueryStrings } from './initialQueryStrings';
import { initialScrollPositions } from './initialScrollPositions';

const initialState: NavigationState = {
  currentPath: { path: '' },
  isScrollPositionEnabled: false,
  queryStrings: cloneDeep(initialQueryStrings),
  scrollPositions: cloneDeep(initialScrollPositions),
};

const reducer: Reducer<NavigationState, NavigationContextAction> = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...initialState };
    case 'setIsScrollPositionEnabled':
      return {
        ...state,
        isScrollPositionEnabled: action.payload.isScrollPositionEnabled,
      };
    case 'setPath':
      return {
        ...state,
        currentPath: { path: action.payload.path },
      };
    case 'setQueryStrings':
      return {
        ...state,
        queryStrings: {
          ...state.queryStrings,
          ...action.payload.queryStrings,
        },
      };
    case 'setScrollPosition':
      return {
        ...state,
        scrollPositions: {
          ...state.scrollPositions,
          [action.payload.scrollPositionKey]: action.payload.scrollPositionValue,
        },
      };

    default:
      throw new Error(`Unknown action type in navigation reducer : ${JSON.stringify(action)}`);
  }
};

const NavigationContextProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <NavigationStateContext.Provider value={state}>
      <NavigationDispatchContext.Provider value={dispatch}>
        {children}
      </NavigationDispatchContext.Provider>
    </NavigationStateContext.Provider>
  );
};

export default NavigationContextProvider;
